import type { Filter } from '@repo/api-codegen';
import { apiWithoutTrailSlash } from '@repo/api-codegen/api/utils';
import { isArray, isNil, size, uniq, uniqBy } from 'lodash-es';
import { LegacyFilterValue } from '../../enums/legacyFilterValue';
import type { IFilterSelection } from '../../interfaces/filterSelection';
import {
	FILTER_OPERATOR_TO_NEGATED_OPERATOR,
	FILTER_OPTIONS_DIVIDER,
} from './constants';
import type {
	FilterDropdownConfigList,
	FilterItem,
	FilterOption,
	FilterValue,
	FilterValueType,
	TopLevelOperatorType,
} from './types';
import { FilterOperator, FilterOptionType } from './types';

export function getValueAsArray(value?: FilterValue | null) {
	if (!value || isNil(value.value)) {
		return [];
	}

	return Array.isArray(value.value) ? value.value : [value.value];
}

export function getSubValueAsArray(value?: FilterValue | null) {
	if (!value || isNil(value.subValue)) {
		return [];
	}

	return Array.isArray(value.subValue) ? value.subValue : [value.subValue];
}

// TODO: this is a temporary list of mappings to keep the searchParams
// backwards compatible with ag-grid (table v1). Once the table v1 is removed
// and we support these filters natively this can be removed as well.
const COMPATIBILITY_MAPPINGS: Record<string, string> = {
	integration: 'integration_id',
	parent: 'parent_id',
};

export function getSearchParamFilters(
	searchParams: URLSearchParams,
	_filterOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[]
) {
	const filterOptionTypes = _filterOptions
		.filter((option) => option !== FILTER_OPTIONS_DIVIDER)
		.map((option) => option.type);

	// These use the legacy format of filters, which is `field: value`.
	const entries = JSON.parse(searchParams.get('filters') ?? '{}');

	const output = Object.entries(entries)
		.filter(([key]) =>
			filterOptionTypes.find(
				(option) =>
					option === key.toLowerCase() ||
					option === COMPATIBILITY_MAPPINGS[key.toLowerCase()]
			)
		)
		.map(([key, value]) => {
			const filterType = filterOptionTypes.find(
				(option) =>
					option === key.toLowerCase() ||
					option === COMPATIBILITY_MAPPINGS[key.toLowerCase()]
			) as FilterOptionType;

			const filterOption = _filterOptions.find(
				(f) => f !== FILTER_OPTIONS_DIVIDER && f.type === filterType
			) as FilterOption;

			if (!filterOption) {
				return null;
			}

			const filterValue: FilterValue = {
				operator: filterOption.filterDropdownConfig.defaultOperator,
				filterType,
				value: value as FilterValueType,
			};

			return filterValue;
		});

	return output.filter(Boolean) as FilterValue[];
}

export async function getFilterValueFromApiCatalogFilter(
	filterOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[],
	filter: Filter,
	isNegate: boolean = false,
	mergeOperands: boolean = true
): Promise<FilterValue[]> {
	const result: FilterValue[] = [];

	const filtersMap = new Map(
		filterOptions
			.filter((f) => f !== FILTER_OPTIONS_DIVIDER)
			.map((f) => [f.type, f])
	);

	for (const filterOption of filterOptions) {
		if (filterOption === FILTER_OPTIONS_DIVIDER) {
			// eslint-disable-next-line no-continue
			continue;
		}

		if (filterOption.filterDropdownConfig.convertFromCatalogFilter) {
			const convertedFilter =
				// eslint-disable-next-line no-await-in-loop
				await filterOption.filterDropdownConfig.convertFromCatalogFilter(
					filter,
					isNegate
				);

			// Check if the filter field matches either the main field or any additional fields
			const additionalFields =
				filterOption.filterDropdownConfig.additionalFields || [];
			const matchesField =
				filter.field &&
				(filter.field === filterOption.field ||
					additionalFields.includes(filter.field));

			if (convertedFilter && matchesField) {
				return [convertedFilter];
			}
		}
	}
	const filterOperands = filter.operands ?? [];
	if (filter.operator === 'and' || filter.operator === 'or') {
		if (filterOperands.length === 0) {
			return [];
		}

		// Detect a table or schema expanded group at this level and try to convert as one
		let remainingOperands: Filter[] = [...filterOperands];
		const levelFields = new Set(remainingOperands.map((op) => op.field));

		if (levelFields.has('table') && levelFields.has('integration_id')) {
			const tableGroupFields = new Set([
				'integration_id',
				'table',
				'schema',
				'database',
			]);
			const tableGroupOperands: Filter[] = remainingOperands.filter(
				(op) => !!op.field && tableGroupFields.has(op.field)
			);
			const tableGroupFilter: Filter = {
				operator: 'and',
				operands: tableGroupOperands,
			};

			// Only attempt table conversion to avoid regressions on other filters
			const tableOption = filterOptions.find(
				(o) => o !== FILTER_OPTIONS_DIVIDER && o.type === FilterOptionType.TABLE
			) as FilterOption | undefined;
			const convertedTable =
				await tableOption?.filterDropdownConfig.convertFromCatalogFilter?.(
					tableGroupFilter,
					isNegate
				);

			if (convertedTable) {
				const toRemove = new Set(tableGroupOperands);
				remainingOperands = remainingOperands.filter((op) => !toRemove.has(op));
				const rest = (
					await Promise.all(
						remainingOperands.map((operand) =>
							getFilterValueFromApiCatalogFilter(
								filterOptions,
								operand,
								isNegate
							)
						)
					)
				).flat();
				return [convertedTable, ...rest];
			}
		}

		if (levelFields.has('schema') && levelFields.has('integration_id')) {
			const schemaGroupFields = new Set([
				'integration_id',
				'schema',
				'database',
			]);
			const schemaGroupOperands: Filter[] = remainingOperands.filter(
				(op) => !!op.field && schemaGroupFields.has(op.field)
			);
			const schemaGroupFilter: Filter = {
				operator: 'and',
				operands: schemaGroupOperands,
			};

			// Only attempt schema conversion to avoid regressions on other filters
			const schemaOption = filterOptions.find(
				(o) =>
					o !== FILTER_OPTIONS_DIVIDER && o.type === FilterOptionType.SCHEMA
			) as FilterOption | undefined;
			const convertedSchema =
				await schemaOption?.filterDropdownConfig.convertFromCatalogFilter?.(
					schemaGroupFilter,
					isNegate
				);

			if (convertedSchema) {
				const toRemove = new Set(schemaGroupOperands);
				remainingOperands = remainingOperands.filter((op) => !toRemove.has(op));
				const rest = (
					await Promise.all(
						remainingOperands.map((operand) =>
							getFilterValueFromApiCatalogFilter(
								filterOptions,
								operand,
								isNegate
							)
						)
					)
				).flat();
				return [convertedSchema, ...rest];
			}
		}

		const parsedOperands: FilterValue[] = (
			await Promise.all(
				remainingOperands.map((operand) =>
					getFilterValueFromApiCatalogFilter(filterOptions, operand, isNegate)
				)
			)
		).flat();

		const typesSet = new Set(parsedOperands.map((o) => o.filterType));
		if (typesSet.size > 1) {
			// Different filter types should not be merged, even if they share the same underlying field
			return parsedOperands;
		}

		const allOperandsForTheSameField =
			uniqBy(parsedOperands, (o) => filtersMap.get(o.filterType)?.field)
				.length === 1;

		const allOperatorSameForOperands =
			uniq(parsedOperands.map((o) => o.operator)).length === 1;
		const isNotDateFilter = parsedOperands.every(
			(o) =>
				o.operator !== FilterOperator.IsOnOrAfter &&
				o.operator !== FilterOperator.IsOnOrBefore
		);
		if (
			!allOperandsForTheSameField ||
			parsedOperands.length === 0 ||
			(!allOperatorSameForOperands && isNotDateFilter)
		) {
			return parsedOperands;
		}

		// override for IsBetween filter
		if (
			parsedOperands.length === 2 &&
			parsedOperands.every(
				(o) =>
					o.operator === FilterOperator.IsOnOrAfter ||
					o.operator === FilterOperator.IsOnOrBefore
			)
		) {
			return [
				{
					operator: FilterOperator.IsBetween,
					filterType: parsedOperands[0].filterType,
					value: [
						parsedOperands[0].value as FilterValueType,
						parsedOperands[1].value as FilterValueType,
					],
					subValue: parsedOperands[0].subValue,
				},
			];
		}

		// Do not merge or(not, not), only merge or(is_set, is_set)
		const isMultipleOrIsSetOperands =
			filter.operator === 'or' &&
			remainingOperands.length > 1 &&
			remainingOperands.every(
				(op) =>
					op.operator === 'is_set' &&
					Boolean(op.field) &&
					(!op.operands || op.operands.length === 0)
			);

		const operators = uniq(parsedOperands.map((o) => o.operator));
		let isNotSetApplied = false;

		if (operators.includes(FilterOperator.isNotSet)) {
			if (operators.length === 1) {
				// if there are multiple parsedOperands, merge them together if the operands are or(is_set, is_set)
				if (
					mergeOperands &&
					parsedOperands.length > 1 &&
					isMultipleOrIsSetOperands
				) {
					const first = parsedOperands[0];
					return [
						{
							operator: FilterOperator.isNotSet,
							filterType: first.filterType,
							value: null,
							subValue: first.subValue ?? null,
							isSetApplied: false,
							isNotSetApplied: true,
						},
					];
				}
				// simple isNotSet filter
				return parsedOperands;
			}

			// isNotSet filter combined with other filters
			if (operators.length > 1) {
				isNotSetApplied = true;
			}
		}

		const operandsWithoutIsNotSet = parsedOperands.filter(
			(o) => o.operator !== FilterOperator.isNotSet
		);

		// combine all operands for the same field into a single filter
		if (mergeOperands) {
			return [
				{
					operator: operandsWithoutIsNotSet[0].operator,
					filterType: operandsWithoutIsNotSet[0].filterType,
					value: operandsWithoutIsNotSet
						.map((o) => o.value as FilterValueType | FilterValueType[])
						.flat(),
					subValue: operandsWithoutIsNotSet
						.map((o) => o.subValue as FilterValueType | FilterValueType[])
						.flat(),
					isNotSetApplied,
				},
			];
		}

		return parsedOperands;
	}

	if (filter.operator === 'not') {
		if (filterOperands.length === 0) {
			return [];
		}

		const parsedOperands = (
			await Promise.all(
				filterOperands.map((operand) =>
					getFilterValueFromApiCatalogFilter(filterOptions, operand, true)
				)
			)
		).flat();

		return parsedOperands;
	}

	if (
		filter.field?.startsWith('quality_flags.') &&
		filter.operator === 'exact' &&
		filter.value === true
	) {
		const flagName = filter.field.replace('quality_flags.', '');

		if (!flagName) {
			return [];
		}

		return [
			{
				operator: isNegate ? FilterOperator.IsNot : FilterOperator.Is,
				filterType: FilterOptionType.QUALITY_FLAGS,
				value: flagName,
			},
		];
	}

	const option = filterOptions.find(
		(o) => o !== FILTER_OPTIONS_DIVIDER && o.field === filter.field
	) as FilterOption;
	const filterValue = filter.value ?? null;
	const filterSubValue = filter.sub_value ?? null;

	if (!option) {
		return [];
	}

	if (filter.operator === 'exact' || filter.operator === 'in') {
		return [
			{
				operator: isNegate ? FilterOperator.IsNot : FilterOperator.Is,
				filterType: option.type,
				value: filterValue,
				subValue: filterSubValue,
			},
		];
	}

	if (filter.operator === 'contains') {
		return [
			{
				operator: isNegate
					? FilterOperator.DoesNotContain
					: FilterOperator.Contains,
				filterType: option.type,
				value: filterValue,
				subValue: filterSubValue,
			},
		];
	}

	if (filter.operator === 'starts_with') {
		return [
			{
				operator: FilterOperator.StartsWith,
				filterType: option.type,
				value: filterValue,
				subValue: filterSubValue,
			},
		];
	}

	if (filter.operator === 'is_set') {
		return [
			{
				operator: isNegate ? FilterOperator.isNotSet : FilterOperator.isSet,
				filterType: option.type,
				value: filterValue,
				subValue: filterSubValue,
				isSetApplied: !isNegate,
				isNotSetApplied: isNegate,
			},
		];
	}

	if (filter.operator === 'gte') {
		return [
			{
				operator: FilterOperator.IsOnOrAfter,
				filterType: option.type,
				value: filterValue,
				subValue: filterSubValue,
			},
		];
	}

	if (filter.operator === 'lte') {
		return [
			{
				operator: FilterOperator.IsOnOrBefore,
				filterType: option.type,
				value: filterValue,
				subValue: filterSubValue,
			},
		];
	}

	if (filter.operator === 'gt') {
		return [
			{
				operator: FilterOperator.IsGreaterThan,
				filterType: option.type,
				value: filterValue,
				subValue: filterSubValue,
			},
		];
	}

	if (filter.operator === 'lt') {
		return [
			{
				operator: FilterOperator.IsLessThan,
				filterType: option.type,
				value: filterValue,
				subValue: filterSubValue,
			},
		];
	}

	return result;
}

/**
 * Converts a list of filter values to a catalog filter.
 * @param filterOptions - The filter options to use.
 * @param filterValues - The filter values to convert.
 * @param topLevelOperator - The top level operator to use. Must only be 'and' or 'or'. This will always be the top level operator of the resulting filter.
 * @returns The resulting filter.
 */
export async function getApiCatalogFilterFromFilterValues(
	filterOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[],
	filterValues: FilterValue[],
	topLevelOperator: TopLevelOperatorType
): Promise<Filter | undefined> {
	// `isArray(filterValues)` is a defensive check to ensure that the legacy
	// filters are properly parsed
	if (size(filterValues) === 0 || !isArray(filterValues)) {
		return undefined;
	}

	const operands: Filter[] = [];

	for (const value of filterValues) {
		const values = Array.isArray(value.value) ? value.value : [value.value];
		const subValues = Array.isArray(value.subValue)
			? value.subValue[0]
			: value.subValue;
		const filterOption = filterOptions.find(
			(f) => f !== FILTER_OPTIONS_DIVIDER && f.type === value.filterType
		) as FilterOption;

		if (!filterOption) {
			// just being defensive - if the filterType passed in is invalid (no longer supported / removed), we should ignore this entry and log an error
			// eslint-disable-next-line no-console
			console.error(`Invalid filterType: ${value.filterType}`);
			// eslint-disable-next-line no-continue
			continue;
		}

		if (
			value.operator === FilterOperator.Is ||
			value.operator === FilterOperator.IsNot
		) {
			const isOperands: Filter[] = (
				await Promise.all(
					values
						.filter((v) => !isNil(v))
						.map(
							(v) =>
								filterOption.filterDropdownConfig.convertToCatalogFilter?.(
									v
								) ?? {
									operands: [],
									field: filterOption.field,
									operator: 'exact',
									value: v,
									sub_value: subValues,
								}
						)
				)
			).filter(Boolean) as Filter[];

			const isSetFilter = filterOption.filterDropdownConfig.isSetOverride ?? {
				operands: [],
				field: filterOption.field,
				operator: 'is_set',
				value: null,
			};

			if (value.isNotSetApplied) {
				isOperands.push({
					operator: 'not',
					operands: [isSetFilter],
				});
			}

			if (value.isSetApplied) {
				isOperands.push(isSetFilter);
			}

			const isFilters: Filter = {
				operator: 'or',
				operands: isOperands,
			};

			if (value.operator === FilterOperator.IsNot) {
				operands.push({
					operator: 'not',
					operands: [isFilters],
				});
			} else {
				operands.push(isFilters);
			}
			// eslint-disable-next-line no-continue
			continue;
		}

		if (
			value.operator === FilterOperator.Contains ||
			value.operator === FilterOperator.DoesNotContain
		) {
			const containsOperands: Filter[] = (
				await Promise.all(
					values.filter(Boolean).map(
						(v) =>
							filterOption.filterDropdownConfig.convertToCatalogFilter?.(v) ?? {
								operands: [],
								field: filterOption.field,
								operator: 'contains',
								value: v,
								sub_value: subValues,
							}
					)
				)
			).filter(Boolean) as Filter[];

			const isSetFilter = filterOption.filterDropdownConfig.isSetOverride ?? {
				operands: [],
				field: filterOption.field,
				operator: 'is_set',
				value: null,
			};

			if (value.isNotSetApplied) {
				containsOperands.push({
					operator: 'not',
					operands: [isSetFilter],
				});
			}

			if (value.isSetApplied) {
				containsOperands.push(isSetFilter);
			}

			const containsFilters: Filter = {
				operator: 'or',
				operands: containsOperands,
			};

			if (value.operator === FilterOperator.DoesNotContain) {
				operands.push({
					operator: 'not',
					operands: [containsFilters],
				});
			} else {
				operands.push(containsFilters);
			}
		}

		if (value.operator === FilterOperator.StartsWith) {
			const startsWithOperands: Filter[] = (
				await Promise.all(
					values.filter(Boolean).map(
						(v) =>
							filterOption.filterDropdownConfig.convertToCatalogFilter?.(v) ?? {
								operands: [],
								field: filterOption.field,
								operator: 'starts_with',
								value: v,
							}
					)
				)
			).filter(Boolean) as Filter[];

			const isSetFilter = filterOption.filterDropdownConfig.isSetOverride ?? {
				operands: [],
				field: filterOption.field,
				operator: 'is_set',
				value: null,
			};

			if (value.isNotSetApplied) {
				startsWithOperands.push({
					operator: 'not',
					operands: [isSetFilter],
				});
			}

			if (value.isSetApplied) {
				startsWithOperands.push(isSetFilter);
			}

			const startsWithFilters: Filter = {
				operator: 'or',
				operands: startsWithOperands,
			};

			operands.push(startsWithFilters);
		}

		if (
			value.operator === FilterOperator.isSet ||
			value.operator === FilterOperator.isNotSet
		) {
			const isSetFilters: Filter = filterOption.filterDropdownConfig
				.isSetOverride ?? {
				operator: 'or',
				operands: [
					{
						operands: [],
						field: filterOption.field,
						operator: 'is_set',
						value: null,
					},
				],
			};
			if (value.operator === FilterOperator.isNotSet) {
				operands.push({
					operator: 'not',
					operands: [isSetFilters],
				});
			} else {
				operands.push(isSetFilters);
			}
		}

		if (value.operator === FilterOperator.IsOnOrAfter && values.length === 1) {
			operands.push({
				operator: 'and',
				operands: [
					{
						operands: [],
						field: filterOption.field,
						operator: 'gte',
						value: values[0],
					},
				],
			});
		}

		if (value.operator === FilterOperator.IsOnOrBefore && values.length === 1) {
			operands.push({
				operator: 'and',
				operands: [
					{
						operands: [],
						field: filterOption.field,
						operator: 'lte',
						value: values[0],
					},
				],
			});
		}

		if (value.operator === FilterOperator.IsBetween && values.length === 2) {
			operands.push({
				operator: 'and',
				operands: [
					{
						operands: [],
						field: filterOption.field,
						operator: 'gte',
						value: values[0],
					},
					{
						operands: [],
						field: filterOption.field,
						operator: 'lte',
						value: values[1],
					},
				],
			});
		}

		if (
			value.operator === FilterOperator.IsGreaterThan &&
			values.length === 1
		) {
			operands.push({
				operator: 'and',
				operands: [
					{
						operands: [],
						field: filterOption.field,
						operator: 'gt',
						value: values[0],
					},
				],
			});
		}

		if (value.operator === FilterOperator.IsLessThan && values.length === 1) {
			operands.push({
				operator: 'and',
				operands: [
					{
						operands: [],
						field: filterOption.field,
						operator: 'lt',
						value: values[0],
					},
				],
			});
		}
	}

	const result: Filter = {
		operator: topLevelOperator,
		operands,
	};

	// Unwrap unnecessary nesting: if top level has only one operand, return that operand
	// This handles cases like: {operator: 'and', operands: [{operator: 'not', ...}]} → {operator: 'not', ...}
	// Note: AND/OR of a single item is semantically equivalent to just that item
	// The top level operator must match the first operand's operator, otherwise it breaks the contract of the function.
	if (
		operands.length === 1 &&
		operands[0] &&
		typeof operands[0] === 'object' &&
		operands[0].operator === topLevelOperator
	) {
		return operands[0];
	}

	return result;
}

const LEGACY_FILTERS_TO_NEW_FILTERS: Record<
	LegacyFilterValue,
	FilterOptionType
> = {
	[LegacyFilterValue.NATIVE_TYPE]: FilterOptionType.NATIVE_TYPE,
	[LegacyFilterValue.INTEGRATION_NAME]: FilterOptionType.INTEGRATION,
	[LegacyFilterValue.DATABASE]: FilterOptionType.DATABASE,
	[LegacyFilterValue.SCHEMA]: FilterOptionType.SCHEMA,
	[LegacyFilterValue.TAGS]: FilterOptionType.TAGS,
	[LegacyFilterValue.PUBLISHED]: FilterOptionType.PUBLISHED,
	[LegacyFilterValue.VERIFIED]: FilterOptionType.VERIFICATION,
	[LegacyFilterValue.PII]: FilterOptionType.PII,
	[LegacyFilterValue.COLLECTIONS]: FilterOptionType.COLLECTIONS,
	[LegacyFilterValue.OWNERS]: FilterOptionType.OWNERS,
	[LegacyFilterValue.SOURCES]: FilterOptionType.SOURCES,
	[LegacyFilterValue.PARENT_ID]: FilterOptionType.PARENT_ID,
	[LegacyFilterValue.RELATED]: FilterOptionType.RELATED,
	[LegacyFilterValue.SLACK_CHANNELS]: FilterOptionType.SLACK_CHANNELS,
	[LegacyFilterValue.QUESTION_STATUS]: FilterOptionType.QUESTION_STATUS,
	[LegacyFilterValue.QUESTION_PRIORITY]: FilterOptionType.QUESTION_PRIORITY,
};

async function getFilterItems(
	filterOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[],
	filterOptionType: FilterOptionType
) {
	const filterOption = filterOptions.find(
		(f) => f !== FILTER_OPTIONS_DIVIDER && f.type === filterOptionType
	) as FilterOption;

	if (!filterOption) {
		return [];
	}

	const filterDropdownConfig =
		filterOption.filterDropdownConfig as FilterDropdownConfigList;

	if (typeof filterDropdownConfig.getItems !== 'function') {
		return filterDropdownConfig.getItems;
	}

	return filterDropdownConfig.getItems();
}

async function getFromSearch(filters: Filter) {
	const res = await fetch(
		`${apiWithoutTrailSlash()}/search/?${new URLSearchParams({
			filters: JSON.stringify(filters),
			page: '1',
			search_term: '',
		}).toString()}`
	);

	if (res.ok) {
		return ((await res.json()).results as { id: string; title: string }[]).map(
			(item) => ({
				label: item.title,
				value: item.id,
			})
		);
	}

	return [];
}

const uuidRegex =
	/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export async function legacyFilterToFilterValue(
	filterOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[],
	filters: Partial<Record<LegacyFilterValue, IFilterSelection>>
): Promise<FilterValue[]> {
	// integrations and schemas have changed between legacy and filters v3
	// for Filters v3 we use the entity ID, but legacy filters use the entity name
	// this method does the correct translation to store the backwards compatible filters in views
	// new schema filters are stored with the schema_id, but legacy filters use the schema name
	const integrations = await getFilterItems(
		filterOptions,
		FilterOptionType.INTEGRATION
	);

	const allSchemaValues = Object.entries(filters)
		.filter(([key]) => key === LegacyFilterValue.SCHEMA)
		.flatMap(([, value]) => value.selectedOptionValues as string[])
		.filter((schema_name) => !schema_name.match(uuidRegex));

	const schemas =
		allSchemaValues.length > 0
			? await getFromSearch({
					operands: [
						{
							operands: [],
							operator: 'in',
							field: 'title',
							value: allSchemaValues,
						},
						{
							operands: [],
							field: 'entity_type',
							operator: 'exact',
							value: 'schema',
						},
					],
					operator: 'and',
				})
			: [];

	const newFilters: FilterValue[] = [];

	Object.entries(filters).forEach(([key, value]) => {
		// Skip filters that have no values and no special flags set.
		// Filters with isSetApplied or isNotSetApplied should be processed even if
		// selectedOptionValues is empty, as they represent "is set" or "is not set" conditions.
		if (
			value.selectedOptionValues.length === 0 &&
			!value.isNotSetApplied &&
			!value.isSetApplied
		) {
			return;
		}

		const filterType =
			LEGACY_FILTERS_TO_NEW_FILTERS?.[key as LegacyFilterValue] ?? key;

		const filterOption = filterOptions.find(
			(f) => f !== FILTER_OPTIONS_DIVIDER && f.type === filterType
		) as FilterOption;

		if (!filterOption) {
			// eslint-disable-next-line no-console
			console.error(
				`Invalid filterType ${filterType} when converting legacy filters to filter values`
			);
			return;
		}

		const { defaultOperator } = filterOption.filterDropdownConfig;

		let filterValue: FilterValue['value'] = null;

		if (filterType === FilterOptionType.INTEGRATION) {
			filterValue = value.selectedOptionValues
				.map((integration) => {
					const integrationItem = integrations.find(
						(i) =>
							i !== FILTER_OPTIONS_DIVIDER &&
							i.label?.toLowerCase() === integration
					) as FilterItem;
					if (integrationItem) {
						return integrationItem?.value;
					}

					return null;
				})
				.filter(Boolean) as FilterValue['value'];
		} else if (filterType === FilterOptionType.SCHEMA) {
			filterValue = value.selectedOptionValues
				.map((schema) => {
					if ((schema ?? '').toString().match(uuidRegex)) {
						return [schema];
					}

					return schemas
						.filter((i) => i.label?.toLowerCase() === schema)
						.map((item) => item.value);
				})
				.flat();
		} else {
			filterValue = value.selectedOptionValues;
		}

		let operator: FilterOperator = FilterOperator.Is;

		if (value.isNotSetApplied && value.selectedOptionValues.length === 0) {
			operator = FilterOperator.isNotSet;
			filterValue = null;
		} else if (value.isSetApplied && value.selectedOptionValues.length === 0) {
			operator = FilterOperator.isSet;
			filterValue = null;
		} else if (value.isInclude) {
			operator = defaultOperator;
		} else if (value.isStartsWithApplied) {
			operator = FilterOperator.StartsWith;
		} else {
			operator =
				FILTER_OPERATOR_TO_NEGATED_OPERATOR?.[defaultOperator] ??
				FilterOperator.IsNot;
		}

		// Unwrap single-value arrays to match original format
		const finalValue =
			Array.isArray(filterValue) && filterValue.length === 1
				? filterValue[0]
				: filterValue;

		const filterValueToAdd: FilterValue = {
			filterType,
			operator,
			value: finalValue,
		};

		// Only add isNotSetApplied/isSetApplied if they're truthy
		if (value.isNotSetApplied) {
			filterValueToAdd.isNotSetApplied = true;
		}
		if (value.isSetApplied) {
			filterValueToAdd.isSetApplied = true;
		}

		newFilters.push(filterValueToAdd);
	});

	return newFilters;
}

export async function filterValueToLegacyFilter(
	filterOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[],
	filters: FilterValue[]
): Promise<Partial<Record<LegacyFilterValue, IFilterSelection>>> {
	// integrations and schemas have changed between legacy and filters v3
	// for Filters v3 we use the entity ID, but legacy filters use the entity name
	// this method does the correct translation to store the backwards compatible filters in views
	// the schema filter is now stored as schema_id and not the schema name as it was previously done
	const integrations = await getFilterItems(
		filterOptions,
		FilterOptionType.INTEGRATION
	);

	return filters.reduce(
		(acc, filter) => {
			const legacyFilterValuePair = Object.entries(
				LEGACY_FILTERS_TO_NEW_FILTERS
			).find(([, value]) => value === filter.filterType);

			const legacyFilterValue: LegacyFilterValue =
				(legacyFilterValuePair?.[0] ?? filter.filterType) as LegacyFilterValue;

			const filterValues = getValueAsArray(filter);
			const selectedOptionValues: (string | boolean)[] = [];
			if (filter.filterType === FilterOptionType.INTEGRATION) {
				filterValues.forEach((value) => {
					const integration = integrations.find(
						(i) => i !== FILTER_OPTIONS_DIVIDER && i.value === value
					) as FilterItem;
					if (integration) {
						selectedOptionValues.push(integration?.label?.toLowerCase());
					}
				});
			} else if (filter.operator !== FilterOperator.isNotSet) {
				selectedOptionValues.push(...(filterValues as (string | boolean)[]));
			}

			return {
				...acc,
				[legacyFilterValue]: {
					isInclude:
						filter.operator !== FilterOperator.IsNot &&
						filter.operator !== FilterOperator.DoesNotContain &&
						filter.operator !== FilterOperator.isNotSet &&
						filter.operator !== FilterOperator.StartsWith,
					isStartsWithApplied: filter.operator === FilterOperator.StartsWith,
					selectedOptionValues,
					isNotSetApplied: filter.isNotSetApplied,
					isSetApplied: filter.isSetApplied,
				},
			};
		},
		{} as Partial<Record<LegacyFilterValue, IFilterSelection>>
	);
}

export function filterExcessDividers(
	options: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[]
): (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[] {
	return options.reduce(
		(acc, option, index) => {
			// Trim separators from start / end
			if (option === FILTER_OPTIONS_DIVIDER && index === 0) return acc;
			if (option === FILTER_OPTIONS_DIVIDER && index === options.length - 1)
				return acc;

			// Trim double separators looking behind
			const prev = options[index - 1];
			if (
				prev === FILTER_OPTIONS_DIVIDER &&
				option === FILTER_OPTIONS_DIVIDER
			) {
				return acc;
			}

			// Otherwise, continue
			return [...acc, option];
		},
		[] as (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[]
	);
}

export function parseFilterValuesFromLocalStorage(
	preferencesLocalStorageKey: string
): Array<FilterValue> {
	try {
		const preferences: FilterValue[] = JSON.parse(
			localStorage.getItem(preferencesLocalStorageKey) ?? '[]'
		);

		return preferences
			?.map((preference) => {
				// we used to store the entire FilterOption object into local storage. We now changed FilterValue to only have a reference to the FilterOption.type instead (as FilterValue.filterType).
				const legacyFilterType = preference as unknown as {
					option: { type: FilterOptionType };
				};

				const filterValue = {
					...preference,
					filterType: preference.filterType ?? legacyFilterType.option.type,
				};

				if (!Object.values(FilterOptionType).includes(filterValue.filterType)) {
					// in case the filterType saved does not exist anymore (changed its definition / removed), we should just ignore this entry and log an error
					// eslint-disable-next-line no-console
					console.error(
						`Invalid filterType in preferencesLocalStorage: ${filterValue.filterType}`
					);
					return null;
				}

				return filterValue;
			})
			.filter(Boolean) as Array<FilterValue>;
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(e);
	}

	return [];
}
